import { RouterProvider } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useSession } from './auth/session';
import { PontNatif } from './natif/PontNatif';
import { creerRouteur } from './routes';

/**
 * Amorçage de l'application.
 *
 * Vit dans son propre module, et non dans `main.tsx`, pour rester montable en test :
 * `main.tsx` touche le document et crée la racine React dès son import. Une page blanche
 * due à l'amorçage lui-même est passée inaperçue tant que rien ne montait le vrai routeur.
 */
export function Application() {
  const session = useSession();

  // Le routeur est construit **une seule fois**. Le recréer à chaque changement de session
  // remettait `RouterProvider` devant un routeur jamais chargé : il ne rendait rien, et
  // l'application affichait une page blanche dès que la restauration de session aboutissait.
  const [routeur] = useState(() => creerRouteur(session));

  // Le contexte garde son identité tant que la session ne change pas : un littéral
  // reconstruit à chaque rendu faisait recharger le routeur sans fin, jusqu'à figer l'onglet.
  const contexte = useMemo(() => ({ session }), [session]);

  // Les gardes de route s'évaluent **au montage**, avec le contexte de ce moment-là. Tant
  // que la session est en cours de restauration, aucune ne peut conclure : monter le
  // routeur tout de suite laissait un visiteur sans session sur l'accueil, où il lisait un
  // échec de chargement au lieu d'une invitation à se connecter.
  //
  // On attend donc que la session soit fixée avant de monter le routeur. C'est plus simple
  // — et plus sûr — que de rejouer les gardes après coup : une invalidation qui redirige
  // relance un chargement, qui redirige à son tour.
  if (session.chargement) {
    return <p className="coquille discret">Chargement…</p>;
  }

  return (
    <>
      {/* Le raccord avec la coque mobile, au-dessus du routeur et hors de lui : il vit
          aussi longtemps que l'application, alors qu'un écran se démonte à chaque
          navigation — l'écouteur du bouton retour d'Android n'y survivrait pas. Il ne rend
          rien, et ne fait rien du tout dans un navigateur. */}
      <PontNatif />
      <RouterProvider router={routeur} context={contexte} />
    </>
  );
}
